import React from "react";
import { Link } from "react-router-dom";
import { InlineMath } from "react-katex";

const CBSEBiology10 = () => {
  return (
    <div style={{ maxWidth: "900px", margin: "40px auto", padding: "20px", fontFamily: "'Nunito', 'Poppins', sans-serif", lineHeight: "1.8", color: "#1e293b" }}>
      
      <div style={{ background: "linear-gradient(135deg, #1e3a5f, #2c5282)", borderRadius: 16, padding: "40px 30px", color: "white", marginBottom: 30, textAlign: "center" }}>
        <h1 style={{ fontSize: "32px", margin: "0 0 10px 0" }}>🧬 CBSE Class 10 Biology — Complete Guide</h1>
        <p style={{ fontSize: "18px", opacity: 0.9, margin: "0 0 20px 0" }}>Life Processes, Reproduction, Heredity, Environment — understand every concept with diagrams and instant AI help.</p>
        <a href="https://wa.me/919731453435?text=Help%20me%20with%20Class%2010%20Biology" target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", padding: "14px 32px", background: "#25D366", color: "white", borderRadius: 50, textDecoration: "none", fontWeight: "bold", fontSize: "16px" }}>
          💬 Ask Eluvent on WhatsApp
        </a>
      </div>

      <div style={{ background: "#f1f5f9", borderRadius: 12, padding: "20px", marginBottom: 30 }}>
        <h2 style={{ margin: "0 0 12px 0", fontSize: "20px" }}>📑 Jump to Chapter</h2>
        <ul style={{ columns: 2, paddingLeft: 20, margin: 0 }}>
          <li><a href="#life" style={{ color: "#2c5282" }}>Life Processes</a></li>
          <li><a href="#reproduction" style={{ color: "#2c5282" }}>How Do Organisms Reproduce?</a></li>
          <li><a href="#heredity" style={{ color: "#2c5282" }}>Heredity and Evolution</a></li>
          <li><a href="#environment" style={{ color: "#2c5282" }}>Our Environment</a></li>
        </ul>
      </div>

      <div id="life" style={{ marginBottom: 40 }}>
        <h2 style={{ borderLeft: "4px solid #10b981", paddingLeft: 12, fontSize: "24px" }}>1. Life Processes</h2>
        <p>Living organisms perform certain basic functions to stay alive — these are called life processes: Nutrition, Respiration, Transportation, and Excretion.</p>
        
        <h3>🔑 Key Concepts</h3>
        <ul>
          <li><strong>Autotrophic Nutrition:</strong> Plants make their own food via photosynthesis: <InlineMath math="6CO_2 + 6H_2O \xrightarrow{light} C_6H_{12}O_6 + 6O_2" /></li>
          <li><strong>Heterotrophic Nutrition:</strong> Animals depend on other organisms for food</li>
          <li><strong>Human Respiratory System:</strong> Nose → Pharynx → Larynx → Trachea → Bronchi → Lungs → Alveoli</li>
          <li><strong>Double Circulation:</strong> Blood passes through the heart twice in one complete cycle</li>
          <li><strong>Nephron:</strong> Functional unit of the kidney — filters blood and forms urine</li>
        </ul>

        <a href="https://wa.me/919731453435?text=Explain%20life%20processes%20class%2010%20biology" target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", padding: "10px 20px", background: "#25D366", color: "white", borderRadius: 20, textDecoration: "none", fontWeight: "bold", fontSize: "14px", marginTop: 10 }}>
          💬 How does photosynthesis work? Ask Eluvent →
        </a>
      </div>

      <div id="reproduction" style={{ marginBottom: 40 }}>
        <h2 style={{ borderLeft: "4px solid #10b981", paddingLeft: 12, fontSize: "24px" }}>2. How Do Organisms Reproduce?</h2>
        <p>Reproduction is the biological process by which new individual organisms are produced. It can be asexual (single parent) or sexual (two parents).</p>
        
        <h3>🔑 Types of Reproduction</h3>
        <ul>
          <li><strong>Asexual:</strong> Binary fission (Amoeba), Budding (Hydra), Fragmentation (Spirogyra), Spore formation (Rhizopus)</li>
          <li><strong>Sexual in Plants:</strong> Pollination → Fertilization → Seed formation</li>
          <li><strong>Human Reproduction:</strong> Male gamete (sperm) + Female gamete (egg) → Zygote → Embryo → Baby</li>
          <li><strong>Contraception Methods:</strong> Barrier (condoms), Chemical (pills), Surgical (vasectomy/tubectomy), IUCD (Copper-T)</li>
        </ul>

        <a href="https://wa.me/919731453435?text=Explain%20reproduction%20class%2010%20biology" target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", padding: "10px 20px", background: "#25D366", color: "white", borderRadius: 20, textDecoration: "none", fontWeight: "bold", fontSize: "14px", marginTop: 10 }}>
          💬 Asexual vs Sexual? Ask Eluvent →
        </a>
      </div>

      <div id="heredity" style={{ marginBottom: 40 }}>
        <h2 style={{ borderLeft: "4px solid #10b981", paddingLeft: 12, fontSize: "24px" }}>3. Heredity and Evolution</h2>
        <p>Heredity is the passing of traits from parents to offspring. Evolution is the gradual change in species over generations.</p>
        
        <h3>🔑 Key Concepts</h3>
        <ul>
          <li><strong>Mendel's Laws:</strong> Law of Dominance, Law of Segregation, Law of Independent Assortment</li>
          <li><strong>Monohybrid Cross:</strong> TT × tt → Tt (all tall in F1), 3:1 ratio in F2</li>
          <li><strong>Sex Determination:</strong> Male = XY, Female = XX. Father determines the sex of the child</li>
          <li><strong>Darwin's Theory:</strong> Natural Selection — "Survival of the Fittest"</li>
        </ul>

        <a href="https://wa.me/919731453435?text=Explain%20heredity%20and%20evolution%20class%2010" target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", padding: "10px 20px", background: "#25D366", color: "white", borderRadius: 20, textDecoration: "none", fontWeight: "bold", fontSize: "14px", marginTop: 10 }}>
          💬 Mendel's experiment? Ask Eluvent →
        </a>
      </div>

      <div id="environment" style={{ marginBottom: 40 }}>
        <h2 style={{ borderLeft: "4px solid #10b981", paddingLeft: 12, fontSize: "24px" }}>4. Our Environment</h2>
        <p>An ecosystem consists of biotic (living) and abiotic (non-living) components interacting with each other.</p>
        
        <h3>🔑 Key Concepts</h3>
        <ul>
          <li><strong>Food Chain:</strong> Grass → Grasshopper → Frog → Snake → Eagle</li>
          <li><strong>Trophic Levels:</strong> Producers (T1) → Primary Consumers (T2) → Secondary Consumers (T3) → Tertiary Consumers (T4)</li>
          <li><strong>10% Law:</strong> Only 10% of energy transfers from one trophic level to the next</li>
          <li><strong>Biodegradable:</strong> Decomposed by microorganisms (paper, food waste)</li>
          <li><strong>Non-Biodegradable:</strong> Persist in environment (plastic, glass, DDT)</li>
        </ul>

        <a href="https://wa.me/919731453435?text=Explain%20our%20environment%20class%2010%20biology" target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", padding: "10px 20px", background: "#25D366", color: "white", borderRadius: 20, textDecoration: "none", fontWeight: "bold", fontSize: "14px", marginTop: 10 }}>
          💬 Food chain vs Food web? Ask Eluvent →
        </a>
      </div>

      {/* FAQ */}
      <div style={{ marginBottom: 40 }}>
        <h2 style={{ borderLeft: "4px solid #10b981", paddingLeft: 12, fontSize: "24px" }}>❓ Frequently Asked Questions</h2>
        
        <div itemScope itemType="https://schema.org/FAQPage">
          <div itemScope itemProp="mainEntity" itemType="https://schema.org/Question" style={{ marginBottom: 16, padding: "16px", background: "#f8fafc", borderRadius: 8 }}>
            <h3 itemProp="name" style={{ fontSize: "16px", margin: "0 0 8px 0" }}>Q: Is Class 10 Biology mostly memorization?</h3>
            <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
              <p itemProp="text" style={{ margin: 0 }}>Understanding concepts is more important than rote memorization. Diagrams help a lot. Eluvent can explain any topic with examples and diagrams on WhatsApp.</p>
            </div>
          </div>

          <div itemScope itemProp="mainEntity" itemType="https://schema.org/Question" style={{ marginBottom: 16, padding: "16px", background: "#f8fafc", borderRadius: 8 }}>
            <h3 itemProp="name" style={{ fontSize: "16px", margin: "0 0 8px 0" }}>Q: Which diagram is most important for CBSE Class 10 Biology?</h3>
            <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
              <p itemProp="text" style={{ margin: 0 }}>Human digestive system, respiratory system, heart, nephron, and the human reproductive system diagrams are frequently asked in exams.</p>
            </div>
          </div>
        </div>
      </div>

      <div style={{ background: "#e0f2fe", borderRadius: 12, padding: "20px", marginBottom: 30, textAlign: "center" }}>
        <h3 style={{ margin: "0 0 12px 0" }}>📚 Explore Other Subjects</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
          <Link to="/cbse-class-10-physics" style={{ padding: "10px 18px", background: "#2c5282", color: "white", borderRadius: 20, textDecoration: "none", fontSize: "14px" }}>⚡ Physics</Link>
          <Link to="/cbse-class-10-chemistry" style={{ padding: "10px 18px", background: "#2c5282", color: "white", borderRadius: 20, textDecoration: "none", fontSize: "14px" }}>🧪 Chemistry</Link>
          <Link to="/cbse-class-10-maths" style={{ padding: "10px 18px", background: "#2c5282", color: "white", borderRadius: 20, textDecoration: "none", fontSize: "14px" }}>📐 Mathematics</Link>
        </div>
      </div>

      <div style={{ textAlign: "center", padding: "30px", background: "linear-gradient(135deg, #1e3a5f, #2c5282)", borderRadius: 16, color: "white" }}>
        <h2 style={{ margin: "0 0 10px 0" }}>🚀 Ready to Ace Biology?</h2>
        <p style={{ margin: "0 0 20px 0", fontSize: "16px" }}>Ask any question on WhatsApp and get instant, clear answers from Eluvent.</p>
        <a href="https://wa.me/919731453435?text=Help%20me%20with%20CBSE%20Class%2010%20Biology" target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", padding: "14px 32px", background: "#25D366", color: "white", borderRadius: 50, textDecoration: "none", fontWeight: "bold", fontSize: "16px" }}>
          💬 Start Learning on WhatsApp
        </a>
      </div>

    </div>
  );
};

export default CBSEBiology10;